import React, { useCallback, useEffect, useRef, useState } from "react";
import MarketplaceCoinIcon from "./MarketplaceCoinIcon";
import { buildViewerAuthorProfile } from "./buildViewerAuthorProfile";
import { useCosmeticPreview } from "./cosmeticPreviewStore";
import { fetchMarketplaceBalance, fetchMarketplaceShop } from "./marketplaceApi";
import { fetchUserProfile } from "./profileApi";
import { DEFAULT_PROFILE_ICON_URL } from "./defaultProfileIcon";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import "./commentAuthorDisplay.css";
import "./marketplace.css";

function formatBalance(n) {
  const v = Math.max(0, Math.trunc(Number(n) || 0));
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export default function MarketplaceCurrencyBadge() {
  const enabled = isPluginEnabled(PLUGIN_IDS.MARKETPLACE);
  const cosmeticPreview = useCosmeticPreview();
  const [balance, setBalance] = useState(null);
  const [authorProfile, setAuthorProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rewardPop, setRewardPop] = useState(null);
  const [pulse, setPulse] = useState(false);
  const popTimerRef = useRef(null);
  const pulseTimerRef = useRef(null);

  const refreshIdentity = useCallback(async () => {
    try {
      const [profile, shop] = await Promise.all([
        fetchUserProfile(),
        fetchMarketplaceShop(),
      ]);
      setAuthorProfile(buildViewerAuthorProfile(profile, shop?.catalog || []));
      if (shop?.balance != null) {
        setBalance(Math.max(0, Math.trunc(Number(shop.balance) || 0)));
      }
    } catch {
      setAuthorProfile(null);
    }
  }, []);

  const refreshBalance = useCallback(async () => {
    if (!isPluginEnabled(PLUGIN_IDS.MARKETPLACE)) {
      setBalance(null);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchMarketplaceBalance();
      setBalance(Number(data?.balance) || 0);
    } catch {
      setBalance(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!isPluginEnabled(PLUGIN_IDS.MARKETPLACE)) {
      setBalance(null);
      setAuthorProfile(null);
      return;
    }
    setLoading(true);
    try {
      await refreshIdentity();
    } catch {
      await refreshBalance();
    } finally {
      setLoading(false);
    }
  }, [refreshBalance, refreshIdentity]);

  const showRewardAnimation = useCallback((amount, nextBalance) => {
    const delta = Math.trunc(Number(amount) || 0);
    if (delta <= 0) return;
    if (nextBalance != null) {
      setBalance(Math.max(0, Math.trunc(Number(nextBalance) || 0)));
    }
    setRewardPop(delta);
    setPulse(true);
    if (popTimerRef.current) clearTimeout(popTimerRef.current);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    popTimerRef.current = setTimeout(() => setRewardPop(null), 1200);
    pulseTimerRef.current = setTimeout(() => setPulse(false), 520);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setBalance(null);
      setAuthorProfile(null);
      return undefined;
    }
    refresh();
    const onPlugins = () => refresh();
    const onBalance = () => refresh();
    const onProfile = () => refreshIdentity();
    const onRewarded = (ev) => {
      const detail = ev?.detail || {};
      showRewardAnimation(detail.amount, detail.balance);
    };
    window.addEventListener("pb-plugins-changed", onPlugins);
    window.addEventListener("pb-marketplace-balance-changed", onBalance);
    window.addEventListener("pb-user-profile-changed", onProfile);
    window.addEventListener("pb-marketplace-rewarded", onRewarded);
    window.addEventListener("pb-impersonation-changed", onBalance);
    return () => {
      window.removeEventListener("pb-plugins-changed", onPlugins);
      window.removeEventListener("pb-marketplace-balance-changed", onBalance);
      window.removeEventListener("pb-user-profile-changed", onProfile);
      window.removeEventListener("pb-marketplace-rewarded", onRewarded);
      window.removeEventListener("pb-impersonation-changed", onBalance);
      if (popTimerRef.current) clearTimeout(popTimerRef.current);
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    };
  }, [enabled, refresh, refreshIdentity, showRewardAnimation]);

  if (!enabled) return null;

  const previewItem = cosmeticPreview?.item;
  const displayName = authorProfile?.displayName || authorProfile?.email || "";
  let avatarUrl = authorProfile?.avatarUrl || null;
  let auraClass = authorProfile?.avatarAuraClass || "";
  let styleClass = authorProfile?.usernameStyleClass || "";
  if (previewItem?.type === "avatar" && previewItem.imageDataUrl) {
    avatarUrl = previewItem.imageDataUrl;
    auraClass = previewItem.auraClass || "";
  }
  if (previewItem?.type === "usernameStyle" && previewItem.cssClass) {
    styleClass = previewItem.cssClass;
  }
  const isDefaultAvatar = !avatarUrl;
  const resolvedAvatarUrl = avatarUrl || DEFAULT_PROFILE_ICON_URL;
  const isPixelCosmetic =
    !isDefaultAvatar && typeof resolvedAvatarUrl === "string" && resolvedAvatarUrl.startsWith("data:image/svg");

  return (
    <div
      className={`pb-marketplace-status${loading && balance === null ? " is-loading" : ""}`}
      aria-label={
        authorProfile
          ? `${displayName}, V-coin balance: ${formatBalance(balance ?? 0)}`
          : `V-coin balance: ${formatBalance(balance ?? 0)}`
      }
    >
      <div
        className={`pb-marketplace-currency pb-marketplace-status-blob${
          pulse ? " is-rewarded" : ""
        }`}
        title="V-coins"
      >
        <MarketplaceCoinIcon size={22} />
        <span className="pb-marketplace-currency-amount">{formatBalance(balance ?? 0)}</span>
        {rewardPop != null ? (
          <span className="pb-marketplace-reward-pop" aria-hidden="true">
            +{formatBalance(rewardPop)}
          </span>
        ) : null}
      </div>

      {authorProfile ? (
        <div className="pb-marketplace-status-name-blob pb-marketplace-status-blob">
          <span className={`pb-marketplace-status-name${styleClass ? ` ${styleClass}` : ""}`}>
            {displayName}
          </span>
        </div>
      ) : null}

      {authorProfile ? (
        <div className="pb-marketplace-status-avatar-blob pb-marketplace-status-blob" title={displayName}>
          <span
            className={`pb-marketplace-status-avatar-shell${auraClass ? ` ${auraClass}` : ""}`}
            aria-hidden="true"
          >
            <span
              className={`pb-marketplace-status-avatar${
                isPixelCosmetic ? " pb-marketplace-status-avatar--pixel" : ""
              }${isDefaultAvatar ? " pb-marketplace-status-avatar--default" : ""}`}
            >
              <img src={resolvedAvatarUrl} alt="" className="pb-marketplace-status-avatar-img" />
            </span>
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** @deprecated use emitMarketplaceRewardFromResponse from marketplaceRewards.js */
export function notifyMarketplaceBalanceChanged() {
  window.dispatchEvent(new CustomEvent("pb-marketplace-balance-changed"));
}
